import { FC, PropsWithChildren, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setIsLoading, setUser } from "@store/authSlice";
import { Hub } from "@aws-amplify/core";
import { fetchAuthSession } from "aws-amplify/auth";
import { AuthService } from "@service/auth.service";
import { MobileService } from "@service/mobile.service";
import { RootState } from "@store/store";
import { useSignOut } from "@hooks/useSignOut";

const authService = AuthService.instance;

const Auth: FC<PropsWithChildren<any>> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const isPatient = useSelector(
    (state: RootState) => state.auth.user?.isPatient,
  );
  const signOut = useSignOut();
  const lastRegisteredPushTokenRef = useRef<string | null>(null);
  const refreshRetryInFlightRef = useRef(false);

  const getUser = useCallback(
    async (redirectToHome = false): Promise<void> => {
      try {
        dispatch(setIsLoading(true));
        const currentUser = await authService.loadCurrentUser();
        dispatch(setUser(currentUser));
        if (redirectToHome) {
          navigate("/");
        }
      } catch (error: any) {
        if (error.name !== "UserUnAuthenticatedException") {
          console.error(error);
        }
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate],
  );

  useEffect(() => {
    if (isPatient) {
      return;
    }
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          await getUser(true);
          break;
        case "signInWithRedirect":
          await getUser();
          break;
        case "signInWithRedirect_failure":
          console.error("Sign in with redirect failure, details: ", payload);
          break;
        case "tokenRefresh_failure": {
          if (refreshRetryInFlightRef.current) {
            await signOut();
            break;
          }

          refreshRetryInFlightRef.current = true;
          try {
            await fetchAuthSession({ forceRefresh: true });
            await getUser();
          } catch (error) {
            console.error("Token refresh retry failed, signing out", error);
            await signOut();
          } finally {
            refreshRetryInFlightRef.current = false;
          }
          break;
        }
      }
    });

    getUser();

    return unsubscribe;
  }, [isPatient, getUser, dispatch, navigate, signOut]);

  useEffect(() => {
    const registerCachedToken = async () => {
      if (!user) {
        lastRegisteredPushTokenRef.current = null;
        return;
      }

      const cachedJson = localStorage.getItem(
        MobileService.PUSH_TOKEN_CACHE_KEY,
      );
      if (!cachedJson) {
        return;
      }

      // Avoid duplicate registration calls when React re-renders.
      if (lastRegisteredPushTokenRef.current === cachedJson) {
        return;
      }

      lastRegisteredPushTokenRef.current = cachedJson;
      await MobileService.instance.registerPushToken(cachedJson);
    };

    void registerCachedToken();
  }, [user]);

  return children;
};

export default Auth;
